import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PictureOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const Banner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBannerId, setEditBannerId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [newImageInputs, setNewImageInputs] = useState([{ id: Date.now(), file: null }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/getbanner`);
      const data = await res.json();
      setBanners(data);
    } catch {
      Swal.fire('Error', 'Failed to load banners.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  const openAddModal = () => {
    setIsEditing(false);
    setExistingImages([]);
    setRemovedImageIds([]);
    setNewImageInputs([{ id: Date.now(), file: null }]);
    setEditBannerId(null);
    setShowModal(true);
  };

  const handleEdit = (banner) => {
    setIsEditing(true);
    setEditBannerId(banner._id);
    setExistingImages(banner.images || []);
    setRemovedImageIds([]);
    setNewImageInputs([{ id: Date.now(), file: null }]);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && newImageInputs.every((i) => !i.file)) {
      Swal.fire('Error', 'Please select at least one image.', 'error');
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    removedImageIds.forEach((id) => formData.append('removeImages', id));
    newImageInputs.forEach((inp) => { if (inp.file) formData.append('images', inp.file); });
    try {
      const url = isEditing ? `${BASEURL}/master/updatebanner/${editBannerId}` : `${BASEURL}/master/createbanner`;
      const res = await fetch(url, { method: isEditing ? 'PUT' : 'POST', body: formData });
      if (!res.ok) throw new Error();
      Swal.fire('Success', isEditing ? 'Banner updated!' : 'Banner added!', 'success');
      setShowModal(false);
      fetchBanners();
    } catch {
      Swal.fire('Error', 'Failed to process request.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Banner?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    await fetch(`${BASEURL}/master/deletebanner/${id}`, { method: 'DELETE' });
    Swal.fire('Deleted!', 'Banner removed.', 'success');
    fetchBanners();
  };

  const columns = [
    {
      title: '#', key: 'idx', width: 55,
      render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span>,
    },
    {
      title: 'BANNER IMAGES', key: 'images',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.images?.map((img) => (
            <img key={img._id} src={`${BASEURL}${img.url}`} alt="banner"
              style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
          ))}
        </div>
      ),
    },
    {
      title: 'COUNT', key: 'count',
      render: (r) => <Tag color="blue" style={{ borderRadius: 50, fontWeight: 600 }}>{r.images?.length || 0} Images</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 120,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Banner Master</h2>
        </div>
        {banners.length === 0 && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}
            style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Banner
          </Button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <Table columns={columns} dataSource={banners} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10 }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Banner' : 'Add Banner'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={520}
      >
        <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
          {isEditing && existingImages.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Existing Images</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {existingImages.map((img) => (
                  <div key={img._id} style={{ position: 'relative' }}>
                    <img src={`${BASEURL}${img.url}`} alt="" style={{ width: 90, height: 55, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={() => {
                      setRemovedImageIds(p => [...p, img._id]);
                      setExistingImages(p => p.filter(i => i._id !== img._id));
                    }} style={{ position: 'absolute', top: -6, right: -6, background: '#e53935', border: 'none', borderRadius: '50%', width: 20, height: 20, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            <PictureOutlined style={{ marginRight: 6, color: '#2874f0' }} />Upload New Images
          </div>
          {newImageInputs.map((inp) => (
            <div key={inp.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input type="file" accept="image/*"
                onChange={(e) => setNewImageInputs(p => p.map(i => i.id === inp.id ? { ...i, file: e.target.files[0] } : i))}
                style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
              {newImageInputs.length > 1 && (
                <button type="button" onClick={() => setNewImageInputs(p => p.filter(i => i.id !== inp.id))}
                  style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '7px 10px', color: '#e53935', cursor: 'pointer', fontSize: 13 }}>✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setNewImageInputs(p => [...p, { id: Date.now(), file: null }])}
            style={{ background: '#e8f1ff', border: '1px solid #2874f0', borderRadius: 4, padding: '6px 14px', color: '#2874f0', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 20 }}>
            + Add More
          </button>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update Banner' : 'Add Banner'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Banner;
